import React, { useEffect, useMemo, useState } from 'react';

import { CircularProgress, Container, makeStyles, Typography } from '@material-ui/core';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

import AmbraContainer from '../../components/ambraContainer/AmbraContainer';
import ImageWrapper from '../../components/common/ImageWrapper';
import VideoWrapper from '../../components/common/VideoWrapper';
import ObjViewer from '../../components/objViewer/ObjViewer';
import PdfViewer from '../../components/pdfViewer/PdfViewer';
import useBlocRefHook from '../../utils/useBlocRef.hook';
import useWindowSize from '../../utils/useWindowSize';

const useStyles = makeStyles({
  mainContainer: {
    alignItems: 'center',
    backgroundColor: (isDesktop) => (isDesktop ? '#7c7c7d' : 'white'),
    color: (isDesktop) => (isDesktop ? 'white' : ''),
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    justifyContent: 'center',
    maxWidth: (isDesktop) => (isDesktop ? '45.3rem' : '100%'),
  },
});

export default () => {
  const [resourceInfo, setResourceInfo] = useState(null);
  const [childResources, setChildResources] = useState([]);
  const [error, setError] = useState(false);

  const { height: windowHeight, width: windowWidth } = useWindowSize();
  const blocRef = useBlocRefHook('viewerBloc');
  const { search } = useLocation();
  const { uuid, token, viewer, caseId } = queryString.parse(search);

  const isDesktop = viewer === 'desktop';

  const classes = useStyles(isDesktop);

  const shouldError = useMemo(() => {
    return !uuid && !caseId;
  }, [uuid, caseId]);

  const renderError = () => (
    <Container component="main" className={classes.mainContainer}>
      <Container component="div" disableGutters>
        <Typography variant="h4" className="mb-1 page-header" align="center">
          Something went wrong loading the resource
        </Typography>
      </Container>
    </Container>
  );

  const renderLoading = () => (
    <Container className={classes.mainContainer}>
      <CircularProgress style={{ color: isDesktop ? 'white' : '#388E3C' }} />
    </Container>
  );

  if (shouldError || !token) {
    return renderError();
  }

  useEffect(() => {
    blocRef.current.getResourceInfo(uuid, caseId, token, true);
    const resourceInfoSub = blocRef.current.resourceInfo$.subscribe(setResourceInfo);
    const errorSub = blocRef.current.error$.subscribe(setError);
    const childResourcesSub = blocRef.current.childResources$.subscribe(setChildResources);

    return () => {
      resourceInfoSub.unsubscribe();
      errorSub.unsubscribe();
      childResourcesSub.unsubscribe();

      document.body.style = undefined; // Remove body background color
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return renderError();
  }

  if (!resourceInfo) {
    return renderLoading();
  }

  if (resourceInfo) {
    switch (resourceInfo.type) {
      case '3d':
        return <ObjViewer dimensions={{ height: '100vh', width: '100vw' }} url={resourceInfo.path} uuid={uuid} shouldUpdateHash />;
      case 'dcm':
        return windowWidth > 180 && windowHeight > 180 ? (
          <div
            style={{
              alignItems: 'center',
              backgroundColor: childResources?.length ? 'white' : 'black',
              display: childResources?.length ? 'block' : 'flex',
              justifyContent: 'center',
              minHeight: '100vh',
              position: childResources?.length ? '' : 'absolute',
              width: childResources?.length ? '100%' : '100vw',
            }}>
            <AmbraContainer
              url={resourceInfo.viewerLink}
              dimensions={{ height: childResources?.length ? 'calc(100vh - 42px)' : '100vh', width: childResources?.length ? '100%' : '100vw' }}
              title={resourceInfo?.description || ''}
              childResources={childResources}
              compactViewer
            />
          </div>
        ) : (
          <Container className={classes.mainContainer} style={{ color: 'white' }}>
            <Container component="div" disableGutters>
              <Typography variant="h4" className="mb-1" align="center" color="inherit">
                Too small for Preview
              </Typography>
            </Container>
          </Container>
        );
      case 'image':
        return (
          <div style={{ alignItems: 'center', backgroundColor: 'black', display: 'flex', height: '100vh', justifyContent: 'center', position: 'absolute', width: '100vw' }}>
            <ImageWrapper url={resourceInfo.path} height="100vh" />
          </div>
        );
      case 'pdf':
        return <PdfViewer url={resourceInfo.path} dimensions={{ height: '100vh', width: '100vw' }} />;
      case 'video': {
        const videoPath = isDesktop ? resourceInfo?.path || resourceInfo?.h264Path : resourceInfo?.h264Path || resourceInfo?.path;
        return (
          <div style={{ alignItems: 'center', backgroundColor: 'black', display: 'flex', height: '100vh', justifyContent: 'center', position: 'absolute', width: '100vw' }}>
            <VideoWrapper url={videoPath} isStaticSize isPlayOnClick />
          </div>
        );
      }
      default:
        setError(true);
        break;
    }
  }

  return null;
};
